<template>
  <div class="contents">
    <div class="container-fluid" v-if="orderData">
      <div class="breadcrumb-main user-member justify-content-sm-between mb-3">
        <h4 class="text-capitalize fw-500 breadcrumb-title">
          <a @click="$router.go(-1)" style="cursor:pointer"><i class="fas fa-arrow-left"></i></a>
          &nbsp; Order {{ order.invoice_number || shortRef(order.order_id) }}
        </h4>
        <span :class="paymentBadge(order.payment_status)">{{ paymentLabel(order.payment_status) }}</span>
      </div>

      <div class="row">
        <div class="col-lg-8">
          <!-- Timeline -->
          <div class="card p-4 mb-3 border bg-white radius-xl">
            <h5 class="mb-3">Timeline Status</h5>
            <div class="order-timeline">
              <div v-for="step in timeline" :key="step.key" class="timeline-step" :class="{ done: step.done, active: step.active }">
                <div class="timeline-dot"><i :class="step.icon"></i></div>
                <div class="timeline-content">
                  <strong>{{ step.label }}</strong>
                  <small v-if="step.time" class="d-block text-muted">{{ step.time }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Customer -->
          <div class="card p-4 mb-3 border bg-white radius-xl">
            <h5>Informasi Customer</h5>
            <div class="row">
              <div class="col-md-6"><small class="text-muted">Nama</small><p class="mb-2"><b>{{ order.customer_name }}</b></p></div>
              <div class="col-md-6"><small class="text-muted">WhatsApp</small><p class="mb-2">{{ order.customer_phone }}</p></div>
              <div class="col-md-6"><small class="text-muted">Email</small><p class="mb-2">{{ order.customer_email || '-' }}</p></div>
              <div class="col-md-6" v-if="order.shipping_address"><small class="text-muted">Alamat</small><p class="mb-2">{{ order.shipping_address }}</p></div>
              <div class="col-12" v-if="order.notes"><small class="text-muted">Catatan</small><p class="mb-0">{{ order.notes }}</p></div>
            </div>
          </div>

          <!-- Items -->
          <div class="card p-4 mb-3 border bg-white radius-xl">
            <h5>Item Pesanan</h5>
            <table class="table table-sm mb-0">
              <thead><tr><th>Produk</th><th>Qty</th><th class="text-right">Subtotal</th></tr></thead>
              <tbody>
                <tr v-for="it in items" :key="it.id">
                  <td>{{ it.product_name }}<br><small class="text-muted" v-if="it.selected_variants">{{ it.selected_variants }}</small></td>
                  <td>{{ it.quantity }}</td>
                  <td class="text-right">{{ formatRupiah(it.subtotal) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr><td colspan="2" class="text-right text-muted">Subtotal</td><td class="text-right">{{ formatRupiah(subtotal) }}</td></tr>
                <tr><td colspan="2" class="text-right text-muted">Ongkir</td><td class="text-right">{{ formatRupiah(order.shipping_cost) }}</td></tr>
                <tr><td colspan="2" class="text-right"><b>Total</b></td><td class="text-right"><b>{{ formatRupiah(order.total_amount) }}</b></td></tr>
              </tfoot>
            </table>
          </div>

          <!-- Shipping -->
          <div v-if="order.shipping_courier || order.shipping_tracking" class="card p-4 mb-3 border bg-white radius-xl">
            <h5>Info Pengiriman</h5>
            <p class="mb-1">Kurir: <b>{{ order.shipping_courier }}</b> {{ order.shipping_service || '' }}</p>
            <p class="mb-0">Resi: <b>{{ order.shipping_tracking || '-' }}</b></p>
          </div>

          <!-- Notification Log -->
          <div class="card p-4 mb-3 border bg-white radius-xl">
            <h5>Log Notifikasi</h5>
            <div v-if="notifLoading" class="text-muted small">Memuat...</div>
            <div v-else-if="notificationLogs.length === 0" class="text-muted">Belum ada notifikasi terkirim.</div>
            <table v-else class="table table-sm mb-0">
              <thead><tr><th>Waktu</th><th>Event</th><th>Channel</th><th>Status</th></tr></thead>
              <tbody>
                <tr v-for="log in notificationLogs" :key="log.id">
                  <td><small>{{ formatTime(log.created_at) }}</small></td>
                  <td><small>{{ log.event_type }}</small></td>
                  <td><span class="badge badge-light">{{ log.channel }}</span></td>
                  <td><span :class="log.status === 'sent' ? 'badge badge-success' : 'badge badge-warning'">{{ log.status }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="card p-4 mb-3 border bg-white radius-xl">
            <h5>Status & Pembayaran</h5>
            <p>Status Order: <span :class="statusBadge(order.status)">{{ statusLabel(order.status) }}</span></p>
            <p>Pembayaran: <span :class="paymentBadge(order.payment_status)">{{ paymentLabel(order.payment_status) }}</span></p>
            <p v-if="order.payment_method">Metode: <b>{{ order.payment_method }}</b></p>
            <p v-if="order.transaction_id">Ref Tripay: <code class="small">{{ order.transaction_id }}</code></p>
            <p v-if="order.starsender_fee">Fee Starsender: <b>{{ formatRupiah(order.starsender_fee) }}</b></p>
            <p v-if="order.shop_owner_amount">Saldo Masuk: <b class="text-success">{{ formatRupiah(order.shop_owner_amount) }}</b></p>

            <hr>
            <h6>Aksi</h6>
            <button v-if="order.payment_status === 'pending'" class="btn btn-success btn-block mb-2" @click="markPaid">
              <i class="fas fa-check"></i> Konfirmasi Pembayaran Manual
            </button>
            <button v-if="order.payment_status === 'paid' && order.status === 'pending'" class="btn btn-info btn-block text-white mb-2" @click="updateStatus('processed')">
              <i class="fas fa-box"></i> Tandai Diproses
            </button>
            <button v-if="order.payment_status === 'paid' && ['pending','processed','processing'].includes(order.status)" class="btn btn-primary btn-block mb-2" @click="openResi">
              <i class="fas fa-truck"></i> Input Resi & Kirim
            </button>
            <button v-if="order.payment_status === 'paid' && order.status !== 'completed' && order.status !== 'cancelled'" class="btn btn-outline-success btn-block mb-2" @click="updateStatus('completed')">
              <i class="fas fa-check-double"></i> Tandai Selesai
            </button>
            <button v-if="order.payment_status === 'paid' && order.status !== 'cancelled'" class="btn btn-warning btn-block mb-2" @click="refundOrder">
              <i class="fas fa-undo"></i> Refund Order
            </button>
            <button v-if="order.payment_status !== 'paid' && order.status !== 'cancelled'" class="btn btn-outline-danger btn-block" @click="cancelOrder">
              <i class="fas fa-times"></i> Batalkan Order
            </button>
          </div>
        </div>
      </div>

      <modal name="resi_modal" :height="'auto'" :adaptive="true">
        <div class="p-4">
          <h4>Input Resi Pengiriman</h4>
          <form @submit.prevent="submitResi">
            <label>Kurir</label>
            <input v-model="resiForm.courier" class="form-control mb-2" placeholder="JNE / J&T / SiCepat" required>
            <label>Service (Opsional)</label>
            <input v-model="resiForm.service" class="form-control mb-2" placeholder="REG / YES">
            <label>Nomor Resi</label>
            <input v-model="resiForm.tracking" class="form-control mb-3" required>
            <button type="submit" class="btn btn-primary">Simpan & Kirim Notif</button>
            <button type="button" class="btn btn-light ml-2" @click="$modal.hide('resi_modal')">Batal</button>
          </form>
        </div>
      </modal>
    </div>

    <div v-else class="text-center p-5">
      <div class="spinner-border text-primary"></div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      orderData: null,
      resiForm: { courier: '', service: '', tracking: '' },
      notificationLogs: [],
      notifLoading: false,
    };
  },
  computed: {
    order() { return this.orderData ? this.orderData.order : {}; },
    items() { return this.orderData ? this.orderData.items : []; },
    subtotal() { return this.items.reduce((s, i) => s + (i.subtotal || 0), 0); },
    timeline() {
      const o = this.order;
      const steps = [
        { key: 'created', label: 'Order Dibuat', icon: 'fas fa-shopping-cart', time: this.formatTime(o.created_at), done: true },
        { key: 'paid', label: 'Pembayaran', icon: 'fas fa-credit-card', time: this.formatTime(o.paid_at), done: !!o.paid_at, active: o.payment_status === 'pending' },
        { key: 'processed', label: 'Diproses', icon: 'fas fa-box', time: null, done: ['processed','processing','shipped','completed'].includes(o.status) },
        { key: 'shipped', label: 'Dikirim', icon: 'fas fa-truck', time: this.formatTime(o.shipped_at), done: !!o.shipped_at || o.status === 'completed' },
        { key: 'completed', label: 'Selesai', icon: 'fas fa-check-circle', time: this.formatTime(o.completed_at), done: o.status === 'completed' },
      ];
      if (o.status === 'cancelled') {
        steps.push({ key: 'cancelled', label: 'Dibatalkan', icon: 'fas fa-times-circle', time: this.formatTime(o.cancelled_at), done: true });
      }
      return steps;
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      try {
        const r = await this.$store.dispatch('shop_order/shop_order_detail', this.$route.params.id);
        this.orderData = r.data.data;
        this.loadNotificationLogs();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Gagal load order');
      }
    },
    async loadNotificationLogs() {
      if (!this.order.shop_id) return;
      this.notifLoading = true;
      try {
        const r = await this.$store.dispatch('shop/shop_notification_log', {
          shopId: this.order.shop_id,
          params: { order_id: this.order.id, limit: 20 },
        });
        this.notificationLogs = r.data.data.logs || [];
      } catch (e) { this.notificationLogs = []; }
      finally { this.notifLoading = false; }
    },
    async markPaid() {
      if (!confirm('Konfirmasi pembayaran manual? Saldo akan masuk ke akun toko.')) return;
      try {
        await this.$store.dispatch('shop_order/shop_order_mark_paid', this.$route.params.id);
        this.$toast.success('Pembayaran dikonfirmasi');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
    },
    async updateStatus(status) {
      try {
        await this.$store.dispatch('shop_order/shop_order_update_status', { id: this.$route.params.id, status });
        this.$toast.success('Status diubah');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
    },
    openResi() {
      this.resiForm.courier = this.order.shipping_courier || '';
      this.resiForm.service = this.order.shipping_service || '';
      this.resiForm.tracking = this.order.shipping_tracking || '';
      this.$modal.show('resi_modal');
    },
    async submitResi() {
      try {
        await this.$store.dispatch('shop_order/shop_order_input_resi', { id: this.$route.params.id, payload: this.resiForm });
        this.$toast.success('Resi disimpan');
        this.$modal.hide('resi_modal');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
    },
    async cancelOrder() {
      const reason = prompt('Alasan pembatalan?');
      if (reason === null) return;
      try {
        await this.$store.dispatch('shop_order/shop_order_cancel', { id: this.$route.params.id, reason });
        this.$toast.success('Order dibatalkan');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal'); }
    },
    async refundOrder() {
      const reason = prompt('Alasan refund? (saldo toko akan dikurangi)');
      if (reason === null) return;
      if (!confirm('Yakin refund order ini?')) return;
      try {
        await this.$store.dispatch('shop_order/shop_order_refund', { id: this.$route.params.id, reason });
        this.$toast.success('Refund berhasil');
        this.load();
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal refund'); }
    },
    shortRef(ref) { return ref && ref.length > 16 ? ref.slice(0, 12) + '...' : ref; },
    formatRupiah(v) { return 'Rp ' + (v || 0).toLocaleString('id-ID'); },
    formatTime(t) { return t ? moment(parseInt(t)).format('DD MMM YYYY HH:mm') : null; },
    statusLabel(s) { return { pending: 'Pending', processed: 'Diproses', processing: 'Diproses', shipped: 'Dikirim', completed: 'Selesai', cancelled: 'Dibatalkan' }[s] || s; },
    paymentLabel(s) { return { pending: 'Belum Bayar', paid: 'Lunas', expired: 'Expired', failed: 'Gagal', refund: 'Refund' }[s] || s; },
    statusBadge(s) {
      return { badge: true, 'badge-warning': s === 'pending', 'badge-info': s === 'processed' || s === 'processing', 'badge-primary': s === 'shipped', 'badge-success': s === 'completed', 'badge-danger': s === 'cancelled' };
    },
    paymentBadge(s) {
      return { badge: true, 'badge-warning': s === 'pending', 'badge-success': s === 'paid', 'badge-danger': s === 'failed' || s === 'expired', 'badge-secondary': s === 'refund' };
    },
  },
};
</script>

<style scoped>
.order-timeline { display: flex; flex-direction: column; gap: 0; }
.timeline-step { display: flex; align-items: flex-start; gap: 12px; padding-bottom: 20px; position: relative; }
.timeline-step:not(:last-child)::before { content: ''; position: absolute; left: 15px; top: 32px; bottom: 0; width: 2px; background: #e5e7eb; }
.timeline-step.done:not(:last-child)::before { background: #667eea; }
.timeline-dot { width: 32px; height: 32px; border-radius: 50%; background: #f3f4f6; color: #9ca3af; display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 1; }
.timeline-step.done .timeline-dot { background: #667eea; color: #fff; }
.timeline-step.active .timeline-dot { background: #f59e0b; color: #fff; box-shadow: 0 0 0 4px rgba(245,158,11,0.2); }
</style>
